import ButtonBase from "@material-ui/core/ButtonBase";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import Popper from "@material-ui/core/Popper";
import { makeStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";
import Typography from "@material-ui/core/Typography";
import FilterListIcon from "@material-ui/icons/FilterList";
import classNames from "classnames";
import React from "react";
import { FormattedMessage } from "react-intl";

import { FilterContent } from ".";
import { IFilter, IFilterElement } from "./types";
import useFilter from "./useFilter";

export interface FilterProps<TFilterKeys extends string = string> {
  currencySymbol: string;
  menu: IFilter<TFilterKeys>;
  onFilterAdd: (filter: Array<IFilterElement<string>>) => void;
  pagePlacement?: boolean;
  onClearPagination?: (boolean) => void;
  sorted?: boolean | undefined;
  showIcon?: boolean;
}

const useStyles = makeStyles(
  theme => ({
    addFilterButton: {
      "&$filterButton": {
        "&:hover, &:focus": {
          backgroundColor: fade(theme.palette.primary.main, 0.1)
        },
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.primary.main}`,
        cursor: "pointer",
        marginBottom: 0,
        marginRight: theme.spacing(2),
        marginTop: 0,
        transition: theme.transitions.duration.short + "ms"
      }
    },
    addFilterButtonActive: {
      "&$addFilterButton": {
        backgroundColor: fade(theme.palette.primary.main, 0.1)
      }
    },
    addFilterIcon: {
      transition: theme.transitions.duration.short + "ms"
    },
    addFilterText: {
      color: theme.palette.primary.main,
      fontSize: 14,
      fontWeight: 600 as 600,
      textTransform: "uppercase"
    },
    filterButton: {
      alignItems: "center",
      backgroundColor: fade(theme.palette.primary.main, 0.6),
      borderRadius: "4px",
      display: "flex",
      height: 40,
      justifyContent: "space-around",
      margin: theme.spacing(2, 1),
      marginLeft: 0,
      padding: theme.spacing(0, 2),
      position: "relative"
    },
    filterIcon: {
      marginRight: "5px"
    },
    paper: {
      "& p": {
        paddingBottom: 10
      },
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
      width: 240
    },
    popover: {
      width: 376,
      zIndex: 3
    },
    rotate: {
      transform: "rotate(180deg)"
    },
    separator: {
      backgroundColor: theme.palette.primary.main,
      display: "inline-block",
      height: 28,
      margin: theme.spacing(0, 1.5, 0, 1),
      width: 1
    }
  }),
  { name: "Filter" }
);

const Filter: React.FC<FilterProps> = props => {
  const {
    currencySymbol,
    menu,
    onFilterAdd,
    pagePlacement,
    onClearPagination,
    sorted,
    showIcon
  } = props;
  const classes = useStyles(props);

  const anchor = React.useRef<HTMLDivElement>();
  const [isFilterMenuOpened, setIsFilterMenuOpened] = React.useState(false);
  const [data, dispatch, reset] = useFilter(menu);

  const isFilterActive = menu.some(filterElement => filterElement.active);

  const hardReset = () => {
    reset();
    if (typeof onClearPagination === "function") {
      onClearPagination(true);
    }
  };

  return (
    <ClickAwayListener
      onClickAway={event => {
        let target = event.target as Node | null;
        if (target && target.nodeType === Node.TEXT_NODE) {
          target = target.parentNode;
        }
        const el = target as Element;

        // If the clicked element is no longer in the document body, it was likely
        // a portal (like the Select dropdown menu) that unmounted upon selection.
        if (el && !document.body.contains(el)) {
          return;
        }

        // Ignore mousedown/mouseup mismatch clicks that bubble to the body
        // (common when portals like Select dropdowns open/close and shift the DOM)
        if (el === document.body || el === document.documentElement) {
          return;
        }

        const isOption = el?.getAttribute?.("role") === "option";
        const isInsideMenu = el?.closest
          ? !!el.closest('[role="option"]') ||
            !!el.closest('[role="listbox"]') ||
            !!el.closest('[role="menu"]') ||
            !!el.closest(".MuiPopover-root") ||
            !!el.closest(".MuiMenu-root")
          : false;

        if (!isOption && !isInsideMenu) {
          setIsFilterMenuOpened(false);
        }
      }}
    >
      <div id="cmpFilter" ref={anchor}>
        <ButtonBase
          id="btnComponentFilter"
          className={classNames(classes.filterButton, classes.addFilterButton, {
            [classes.addFilterButtonActive]:
              isFilterMenuOpened || isFilterActive
          })}
          onClick={() => setIsFilterMenuOpened(!isFilterMenuOpened)}
        >
          {showIcon && (
            <FilterListIcon
              id="icnFilterList"
              color="primary"
              className={classes.filterIcon}
            />
          )}
          <Typography id="cmpAddFilter" className={classes.addFilterText}>
            <FormattedMessage
              id="txtAddFilter"
              defaultMessage="Filters"
              description="button"
            />
          </Typography>
          {isFilterActive && (
            <>
              <span id="txtFilterActive" className={classes.separator} />
              <Typography
                id="cmpFilterActive"
                className={classes.addFilterText}
              >
                {menu.reduce(
                  (acc, filterElement) => acc + (filterElement.active ? 1 : 0),
                  0
                )}
              </Typography>
            </>
          )}
        </ButtonBase>
        <Popper
          id="cmpFilterMenu"
          className={classes.popover}
          open={isFilterMenuOpened}
          anchorEl={anchor.current}
          transition
          disablePortal
          placement={pagePlacement ? "left-start" : "bottom-start"}
          modifiers={{
            flip: {
              enabled: false
            },
            hide: {
              enabled: false
            },
            preventOverflow: {
              boundariesElement: "scrollParent",
              enabled: false
            }
          }}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "right top" : "right bottom"
              }}
            >
              <FilterContent
                currencySymbol={currencySymbol}
                filters={data}
                onClear={hardReset}
                onFilterPropertyChange={dispatch}
                onSubmit={() => {
                  onFilterAdd(data);
                  setIsFilterMenuOpened(false);
                }}
                sorted={sorted}
              />
            </Grow>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  );
};
Filter.displayName = "Filter";
export default Filter;
